import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, muted, table } from "../../ui/record";
import { archiveModal, area, field, pills, refuse, select } from "../clients/forms";

// Settings › Week, overtime & PTO, the paid time off part (brief 1E-11, mockup v61 settings/time):
// the Paid time off and sick leave card (the company's own policies: add, change, archive with a
// reason; the seeded Sick and safe leave with its note), Holidays (a list per year, for holiday
// pay), and the Balances card (person, policy, earned, used, balance, requested). Every change is
// in the history. Admin only.

interface Choice {
  value: string;
  label: string;
}
interface PolicyForm {
  name: string;
  kind: string;
  earnHow: string;
  earnHours: string;
  perHours: string;
  yearCap: string;
  balanceCap: string;
  carryOver: string;
  carryHours: string;
  waitingDays: string;
  lowHours: string;
  appliesTo: string;
  appliesKinds: string[];
  appliesJobs: string[];
  paid: boolean;
  multiplier: string;
  note: string;
}
export interface PtoSettings {
  today: string;
  policies: {
    id: string;
    name: string;
    kind: string;
    note: string | null;
    earned: string;
    cap: string;
    carries: string;
    who: string;
    more: string;
    form: PolicyForm;
  }[];
  holidays: { year: string; days: { id: string; day: string; date: string; name: string }[] }[];
  hasHolidayPay: boolean;
  balances: {
    person: string;
    policy: string;
    earned: string;
    used: string;
    balance: string;
    requested: string | null;
    unpaid: boolean;
  }[];
  minimumWage: string | null;
  choices: { kinds: Choice[]; earnHow: Choice[]; people: Choice[]; jobs: Choice[] };
}

const BLANK: PolicyForm = {
  name: "",
  kind: "pto",
  earnHow: "per_period",
  earnHours: "",
  perHours: "",
  yearCap: "",
  balanceCap: "",
  carryOver: "none",
  carryHours: "",
  waitingDays: "0",
  lowHours: "",
  appliesTo: "all",
  appliesKinds: [],
  appliesJobs: [],
  paid: true,
  multiplier: "1.5",
  note: "",
};

const pairs = (c: Choice[]) => c.map((x) => [x.value, x.label] as [string, string]);

/** Add a policy, or change one (its kind stays). */
function policyModal(s: PtoSettings, done: (s: PtoSettings) => void, id?: string) {
  const had = id ? s.policies.find((p) => p.id === id)?.form : null;
  const f = had ?? BLANK;
  const name = field("Name", f.name, { maxlength: "80" });
  const kind = select("Kind", pairs(s.choices.kinds), f.kind);
  kind.input.disabled = !!had;
  const how = select("How it's earned", pairs(s.choices.earnHow), f.earnHow);
  const earn = field("Hours earned", f.earnHours, { type: "number", min: "0", step: "0.25" });
  const per = field("Per hours worked", f.perHours, { type: "number", min: "0", step: "0.25" });
  const yearCap = field("Cap a year (hours)", f.yearCap, { type: "number", min: "0" });
  const balCap = field("Balance cap (hours)", f.balanceCap, { type: "number", min: "0" });
  const carry = select(
    "Carries over",
    [
      ["none", "None"],
      ["up_to", "Up to a number of hours"],
      ["all", "All"],
    ],
    f.carryOver,
  );
  const carryHours = field("Hours that carry over", f.carryHours, { type: "number", min: "0" });
  const wait = field("Usable after (days)", f.waitingDays, { type: "number", min: "0" });
  const low = field("None earned in two weeks under (hours)", f.lowHours, {
    type: "number",
    min: "0",
  });
  const paid = el("input", { type: "checkbox", checked: f.paid });
  const paidEl = el("label", { class: "ptask" }, paid, " Paid");
  const mult = field("Paid at (times the rate)", f.multiplier, {
    type: "number",
    min: "1",
    step: "0.1",
  });
  const who = select(
    "Who",
    [
      ["all", "Everyone"],
      ["kinds", "Kinds of people"],
      ["jobs", "Jobs"],
    ],
    f.appliesTo,
  );
  const kinds = pills("Kinds of people", "checkbox", pairs(s.choices.people), f.appliesKinds);
  const jobs = pills("Jobs", "checkbox", pairs(s.choices.jobs), f.appliesJobs);
  const note = area("Note (optional)", f.note);
  const show = () => {
    const holiday = kind.input.value === "holiday";
    for (const x of [how, earn, yearCap, balCap, carry, wait]) x.el.hidden = holiday;
    paidEl.hidden = holiday;
    mult.el.hidden = !holiday;
    per.el.hidden = holiday || how.input.value !== "per_hours";
    low.el.hidden = holiday || how.input.value !== "per_hours";
    carryHours.el.hidden = holiday || carry.input.value !== "up_to";
    kinds.el.hidden = who.input.value !== "kinds";
    jobs.el.hidden = who.input.value !== "jobs";
  };
  for (const x of [kind, how, carry, who]) x.input.addEventListener("change", show);
  show();
  const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
  const m = openModal({
    title: had ? `Change ${had.name}` : "Add a policy",
    sub: had
      ? "Balances are worked out again from validated visits and shifts."
      : "Hours are earned from validated visits and shifts only.",
    wide: true,
    body: [
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el("div", { class: "grid2", style: "gap:8px" }, name.el, kind.el),
        el("div", { class: "grid2", style: "gap:8px" }, how.el, earn.el, per.el, mult.el),
        el(
          "div",
          { class: "grid2", style: "gap:8px" },
          yearCap.el,
          balCap.el,
          carry.el,
          carryHours.el,
        ),
        el("div", { class: "grid2", style: "gap:8px" }, wait.el, low.el),
        paidEl,
        who.el,
        kinds.el,
        jobs.el,
        note.el,
      ),
    ],
    actions: [go],
  });
  name.input.focus();
  go.addEventListener("click", async () => {
    go.disabled = true;
    const data = {
      name: name.input.value,
      kind: kind.input.value,
      earnHow: how.input.value,
      earnHours: earn.input.value,
      perHours: per.input.value,
      yearCap: yearCap.input.value,
      balanceCap: balCap.input.value,
      carryOver: carry.input.value,
      carryHours: carryHours.input.value,
      waitingDays: wait.input.value,
      lowHours: low.input.value,
      appliesTo: who.input.value,
      appliesKinds: kinds.values(),
      appliesJobs: jobs.values(),
      paid: paid.checked,
      multiplier: mult.input.value,
      note: note.input.value,
    };
    const r = had
      ? await api<PtoSettings>("PUT", `/api/settings/pto/policies/${id}`, data)
      : await api<PtoSettings>("POST", "/api/settings/pto/policies", data);
    go.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast(had ? "Policy saved" : "Policy added", data.name);
    done(r.body);
  });
}

/** Add a holiday: its day and name. */
function holidayModal(done: (s: PtoSettings) => void) {
  const day = field("Day", "", { type: "date" });
  const name = field("Name", "", { maxlength: "80", placeholder: "Thanksgiving" });
  const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Add");
  const m = openModal({
    title: "Add a holiday",
    sub: "Holiday pay is paid for work on the listed days.",
    body: [el("div", { class: "form", style: "max-width:none" }, day.el, name.el)],
    actions: [go],
  });
  day.input.focus();
  go.addEventListener("click", async () => {
    go.disabled = true;
    const r = await api<PtoSettings>("POST", "/api/settings/holidays", {
      day: day.input.value,
      name: name.input.value,
    });
    go.disabled = false;
    if (!r.ok) return refuse(m.note, r.body.error);
    m.close();
    toast("Holiday added", name.input.value);
    done(r.body);
  });
}

/** The three cards, drawn into host, loading and redrawing themselves. */
export async function ptoCards(host: HTMLElement, first?: PtoSettings) {
  let s: PtoSettings;
  if (first) s = first;
  else {
    const res = await api<PtoSettings>("GET", "/api/settings/pto");
    if (!res.ok) return;
    s = res.body;
  }
  const redraw = (next: PtoSettings) => {
    s = next;
    draw();
  };
  const reload = async () => {
    const res = await api<PtoSettings>("GET", "/api/settings/pto");
    if (res.ok) redraw(res.body);
  };
  function draw() {
    const policies = card(
      el("span", {}, icon("calendar"), " Paid time off and sick leave"),
      "Add the company's own policies. Hours are earned from validated visits and shifts only.",
      [ib("plus", "Add a policy", () => policyModal(s, redraw))],
      s.policies.length
        ? table(
            [
              {
                h: "Policy",
                f: (p) =>
                  el(
                    "span",
                    {},
                    el("b", {}, p.name),
                    p.note ? el("div", { class: "small muted" }, p.note) : null,
                    p.more ? el("div", { class: "small muted" }, p.more) : null,
                  ),
              },
              { h: "Earned", f: (p) => p.earned },
              { h: "Cap", f: (p) => p.cap },
              { h: "Carries over", f: (p) => p.carries },
              { h: "Who", f: (p) => p.who },
              {
                h: "",
                f: (p) =>
                  el(
                    "span",
                    { class: "rowacts" },
                    ib("pencil", `Edit ${p.name}`, () => policyModal(s, redraw, p.id)),
                    ib("archive", `Archive ${p.name}`, () =>
                      archiveModal(p.name, `/api/settings/pto/policies/${p.id}/archive`, reload),
                    ),
                  ),
              },
            ],
            s.policies,
            undefined,
            "Paid time off and sick leave",
          )
        : el("p", { class: "muted" }, "No policies yet."),
    );
    policies.setAttribute("aria-label", "Paid time off and sick leave");
    const holidays = card(
      el("span", {}, icon("calendar"), " Holidays"),
      s.hasHolidayPay
        ? "Holiday pay is paid for work on these days."
        : "Add Holiday pay as a policy to pay for work on these days.",
      [ib("plus", "Add a holiday", () => holidayModal(redraw))],
      ...(s.holidays.length
        ? s.holidays.map((y) =>
            el(
              "div",
              {},
              el("h3", { style: "margin:8px 0 4px" }, y.year),
              table(
                [
                  { h: "Day", f: (d) => d.date },
                  { h: "Holiday", f: (d) => el("b", {}, d.name) },
                  {
                    h: "",
                    f: (d) =>
                      ib("archive", `Take ${d.name} off the list`, () =>
                        archiveModal(d.name, `/api/settings/holidays/${d.id}/archive`, reload),
                      ),
                  },
                ],
                y.days,
                undefined,
                `Holidays ${y.year}`,
              ),
            ),
          )
        : [el("p", { class: "muted" }, "No holidays yet.")]),
    );
    holidays.setAttribute("aria-label", "Holidays");
    const balances = card(
      el("span", {}, icon("people"), " Balances"),
      null,
      [],
      s.balances.length
        ? table(
            [
              { h: "Person", f: (b) => el("b", {}, b.person) },
              { h: "Policy", f: (b) => b.policy },
              { h: "Earned", f: (b) => b.earned },
              { h: "Used", f: (b) => b.used },
              { h: "Balance", f: (b) => el("b", {}, b.balance) },
              {
                h: "Requested",
                f: (b) =>
                  b.requested ? chip(b.requested, b.unpaid ? "warn" : "info") : muted("–"),
              },
            ],
            s.balances,
            undefined,
            "Balances",
          )
        : el("p", { class: "muted" }, "No hours earned yet."),
      el(
        "p",
        { class: "small muted" },
        "Approved time off shows on the schedule and goes to payroll as its own line.",
      ),
    );
    balances.setAttribute("aria-label", "Balances");
    host.replaceChildren(policies, balances, holidays);
  }
  draw();
}
